import logger from "@/lib/logger";
import newrelic from "@/lib/monitoring/newrelic";
import { MonarchPayload, MonarchApiServerResponse, WebContext } from "./types";
import { monarch } from "@redventures/cohesion-utils-react";
import { MONARCH_SOURCE_ID } from "./constants";
import getDapiRandomId from "@/lib/dapi/utils/getDapiRandomId";

// Client-side monarch config - static values only
const monarchConfig = {
  sourceId: MONARCH_SOURCE_ID,
};

const mergeWebContext = (
  base: Partial<WebContext> | undefined,
  override: Partial<WebContext> | undefined,
): WebContext => ({
  ...base,
  ...override,
  cohesionConsent: override?.cohesionConsent ?? base?.cohesionConsent ?? false,
  page: {
    ...base?.page,
    ...override?.page,
  },
});

const getCurrentRequestContext = (): MonarchPayload["cohesionPayload"] => {
  if (typeof window === "undefined") return undefined;

  const requestUrl = window.location.href;

  return {
    firstRequestUrl: requestUrl,
    firstReferrerUrl: document.referrer || undefined,
    webContext: mergeWebContext(window._Cohesion?.webContext, {
      page: {
        title: document.title,
        url: requestUrl,
      },
    }),
  };
};

const mergeCohesionPayload = (
  currentPayload: MonarchPayload["cohesionPayload"],
  payloadOverride: MonarchPayload["cohesionPayload"],
): MonarchPayload["cohesionPayload"] | undefined => {
  if (!currentPayload && !payloadOverride) return undefined;

  const mergedWebContext =
    currentPayload?.webContext || payloadOverride?.webContext
      ? mergeWebContext(currentPayload?.webContext, payloadOverride?.webContext)
      : undefined;

  return {
    ...currentPayload,
    ...payloadOverride,
    ...(mergedWebContext && { webContext: mergedWebContext }),
  };
};

/**
 * Client-side Monarch API function using cohesion-utils
 * Use this for browser-side evaluations
 */
const monarchApiClient = (
  ruleId: string,
  payload: MonarchPayload,
): Promise<MonarchApiServerResponse> =>
  new Promise((resolve, reject) => {
    monarch(
      "rule",
      ruleId,
      payload,
      { sourceId: monarchConfig.sourceId },
      (err, result) => {
        if (err) reject(err);
        else resolve(result);
      },
    );
  });

/**
 * Client-side Monarch API function for browser evaluations
 * This function uses the cohesion-utils-react library
 */
export async function callMonarchApi(
  ruleId: string,
  payload: MonarchPayload,
): Promise<MonarchApiServerResponse | null> {
  try {
    const dapiRandomId = getDapiRandomId();
    const cohesionPayload = mergeCohesionPayload(
      getCurrentRequestContext(),
      payload.cohesionPayload,
    );
    const expandedPayload = {
      ...payload,
      ...(dapiRandomId && { dapiRandomId }),
      ...(cohesionPayload && { cohesionPayload }),
    };

    return await monarchApiClient(ruleId, expandedPayload);
  } catch (error) {
    logger.error(`Error calling Monarch API:`, error);
    newrelic.noticeError(error as Error, {
      message: "Error calling Monarch API",
    });
    return null;
  }
}
